import React, { useState } from 'react';
import AzureMonthChart from './AzureMonthChart';
import {
  Arrow, AvailabilityTable, CategoryName, Kpi, OpenAndClosed, PaceColumn, RateTable, Tone
} from './BriefingParts';
import {
  DASH, SYMPTOMS, CAT_SWATCH, cleanOpenDetail, cutoverMonthNote, fmt1, fmtDay, fmtDayLong, fmtInt, openCountText, pairTone,
  plural, shortOpenStatus, whenLines
} from './briefingFormat';

const VIEW_BUTTONS = [
  { key: 'month', label: 'Monthly' },
  { key: 'azure', label: 'Azure pre / post' },
  { key: 'quarter', label: 'Quarterly' }
];

const isNum = (value) => value != null && value !== '' && Number.isFinite(Number(value));
const show = (value, suffix = '') => (isNum(value) ? `${Number(value) % 1 === 0 ? Number(value) : value}${suffix}` : DASH);

/** Change between two period rows, signed, coloured by whether it is better. */
const Mark = ({ prev, next, lowerBetter, suffix = '' }) => {
  if (!isNum(prev) || !isNum(next)) return <>{DASH}</>;
  const change = Math.round((Number(next) - Number(prev)) * 10) / 10;
  const text = `${change > 0 ? '+' : ''}${Math.abs(change % 1) < 0.05 ? Math.round(change) : change.toFixed(1)}${suffix}`;
  return <Tone tone={pairTone(prev, next, lowerBetter ? 'lower' : 'higher')}>{text}</Tone>;
};


/** Sentence above the KPIs, built from the first and last rows of the chosen view. */
const ViewLead = ({ name, rows }) => {
  const first = rows[0];
  const last = rows[rows.length - 1];
  const rate = (
    <Tone tone={pairTone(first.rate, last.rate)}>{fmt1(first.rate)} per 30 days then {fmt1(last.rate)}</Tone>
  );
  const median = (
    <Tone tone={pairTone(first.median, last.median)}>{fmt1(first.median)} d then {fmt1(last.median)} d</Tone>
  );
  const high = <Tone tone={pairTone(first.high, last.high)}>{first.high} then {last.high}</Tone>;
  const open = <Tone tone={pairTone(first.open, last.open)}>{last.open}</Tone>;
  const down = <Tone tone={pairTone(first.down, last.down)}>{last.down}</Tone>;
  const stillOpen = <>with {open} {plural(last.open, 'case', 'cases')} still open.</>;

  if (name === 'azure') {
    const fewer = isNum(last.rate) && isNum(first.rate) && Number(last.rate) <= Number(first.rate);
    return (
      <p className="lead">
        {fewer ? 'Fewer' : 'More'} issues after the move to Azure, {rate}, median close {median}, critical or high{' '}
        {high}, with {down} availability {plural(last.down, 'event', 'events')} since the move and {open}{' '}
        {plural(last.open, 'case', 'cases')} still open.
      </p>
    );
  }
  return (
    <p className="lead">
      Issues ran from {rate} ({first.label} to {last.label}), median close {median}, critical or high {high},{' '}
      {stillOpen}
    </p>
  );
};

/** Wording under the measure table; dates come from the briefing's 2026 window. */
const viewCopy = (name, rows, periods) => {
  const last = rows[rows.length - 1];
  const start = fmtDayLong(periods.y26.start);
  const end = fmtDayLong(periods.y26.end);
  const openText = last.open > 0
    ? ` and still has ${last.open} open ${plural(last.open, 'case', 'cases')}`
    : '';
  const tail = 'Rates are per 30 days. Time to close uses closed cases only and includes time waiting on the client. '
    + 'Green is better for the service. Red is worse.';
  const cutover = fmtDayLong(periods.azureCutover);
  if (name === 'month') {
    return {
      title: 'By month opened',
      note: `${rows[0].label} starts ${start}. ${last.label} stops on ${end}${openText}. ${tail}`
    };
  }
  if (name === 'quarter') {
    return {
      title: 'By quarter opened',
      note: `${rows[0].label} starts ${start}. ${last.label} stops on ${end}${openText}. ${tail}`
    };
  }
  return {
    title: `Before and after ${cutover}`,
    note: `${cutover} is counted in After Azure (on or after the move). ${tail}`
  };
};

const AzureTable = ({ first, last }) => {
  const line = (label, a, b, prev, next, lowerBetter, suffix) => (
    <tr key={label}>
      <td>{label}</td>
      <td>{a}</td>
      <td>{b}</td>
      <td><Mark prev={prev} next={next} lowerBetter={lowerBetter} suffix={suffix} /></td>
    </tr>
  );
  return (
    <table className="cmp metric-table">
      <thead>
        <tr><th>Measure</th><th>{first.label}</th><th>{last.label}</th><th>Change</th></tr>
      </thead>
      <tbody>
        {line('Issues reported', first.n, last.n, first.n, last.n, true, '')}
        {line('Issues per 30 days', show(first.rate), show(last.rate), first.rate, last.rate, true, '')}
        {line('Median time to close', show(first.median, ' days'), show(last.median, ' days'), first.median, last.median, true, ' d')}
        {line('Mean time to close', show(first.mean, ' days'), show(last.mean, ' days'), first.mean, last.mean, true, ' d')}
        {line('Closed within 7 days', show(first.within7, '%'), show(last.within7, '%'), first.within7, last.within7, false, ' pp')}
        {line('Still open after 2 weeks', show(first.over14, '%'), show(last.over14, '%'), first.over14, last.over14, true, ' pp')}
        {line('Critical or high', first.high, last.high, first.high, last.high, true, '')}
        {line('Service unavailable', first.down, last.down, first.down, last.down, true, '')}
      </tbody>
    </table>
  );
};

const PeriodTable = ({ rows }) => (
  <table className="nums metric-table">
    <thead>
      <tr>
        <th>Period</th><th>Issues</th><th>Per 30 days</th><th>Median close</th><th>Mean close</th>
        <th>Within 7 days</th><th>Open after 2 weeks</th><th>Critical or high</th><th>Unavailable</th><th>Open now</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.label}>
          <td>{row.label}{row.note ? <div className="note">{row.note}</div> : null}</td>
          <td>{row.n}</td>
          <td>{show(row.rate)}</td>
          <td>{show(row.median, ' d')}</td>
          <td>{show(row.mean, ' d')}</td>
          <td>{show(row.within7, '%')}</td>
          <td>{show(row.over14, '%')}</td>
          <td>{row.high}</td>
          <td>{row.down}</td>
          <td>{row.open}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ViewKpis = ({ first, last }) => (
  <div className="kpis">
    <Kpi
      value={<Arrow prev={first.rate} next={last.rate} />}
      label="Issues per 30 days"
      note={<>{first.label} &middot; {last.label}</>}
    />
    <Kpi
      value={<Arrow prev={first.median} next={last.median} suffix=" d" />}
      label="Median time to close"
      note={<>Mean <Arrow prev={first.mean} next={last.mean} suffix=" days" /></>}
    />
    <Kpi
      value={<Arrow prev={first.within7} next={last.within7} suffix="%" betterWhen="higher" />}
      label="Closed within 7 days"
      note={<>Still open after 2 weeks: <Arrow prev={first.over14} next={last.over14} suffix="%" /></>}
    />
    <Kpi
      value={<Tone tone={pairTone(first.open, last.open)}>{last.open}</Tone>}
      label="Still open"
      note={last.label}
    />
  </div>
);

const VolumeBars = ({ months }) => {
  const max = Math.max(1, ...months.map((row) => row.n || 0));
  return (
    <div>
      {months.map((row) => (
        <div className="month" key={row.label}>
          <div>{row.label}</div>
          <div className="stack" style={{ width: `${Math.round(((row.n || 0) / max) * 100)}%` }}>
            <div className="a" style={{ width: '100%' }} />
          </div>
          <div>
            {fmtInt(row.n)} cases{row.high > 0 ? ` · ${fmtInt(row.high)} critical or high` : ''}
            {row.note ? <> <span className="note">({row.note})</span></> : null}
            {row.median == null ? null : <><br /><span className="note">{fmt1(row.median)} d median</span></>}
          </div>
        </div>
      ))}
    </div>
  );
};

export const AzureSummary = ({ briefing, onStudy, activeSymptom = '' }) => {
  const [view, setView] = useState('azure');
  const az = briefing.azure || {};
  const views = az.views || {};
  const rows = views[view] || [];
  const first = rows[0];
  const last = rows[rows.length - 1];
  const copy = first && last ? viewCopy(view, rows, briefing.periods) : null;
  const months = views.month || [];
  return (
    <section>
      <div className="viewbar" role="tablist" aria-label="How to read the measures">
        {VIEW_BUTTONS.map((button) => (
          <button
            type="button"
            key={button.key}
            role="tab"
            aria-selected={view === button.key ? 'true' : 'false'}
            onClick={() => setView(button.key)}
          >
            {button.label}
          </button>
        ))}
      </div>
      {first && last ? (
        <>
          <ViewLead name={view} rows={rows} />
          <ViewKpis first={first} last={last} />
          <h2>{copy.title}</h2>
          <div className="metric-wrap">
            {view === 'azure' ? <AzureTable first={first} last={last} /> : <PeriodTable rows={rows} />}
          </div>
          <p className="note">{copy.note}</p>
        </>
      ) : (
        <p className="note">No measures for this view.</p>
      )}
      <OpenAndClosed briefing={briefing} prefix="az" />
      <h2 id="az-months">Issues opened each month</h2>
      {cutoverMonthNote(briefing.periods) ? <p className="note">{cutoverMonthNote(briefing.periods)}</p> : null}
      <div className="chart-card">
        <AzureMonthChart stacks={az.monthStacks} onStudy={onStudy} />
        <div className="legend">
          {SYMPTOMS.map((symptom) => (
            <button
              type="button"
              className={`legend-btn${activeSymptom === symptom.key ? ' active' : ''}`}
              key={symptom.key}
              onClick={() => onStudy(symptom.key, '')}
            >
              <i className="swatch" style={{ background: CAT_SWATCH[symptom.label] }} />
              {symptom.label}
            </button>
          ))}
        </div>
      </div>
      {months.length ? (
        <>
          <h2 id="az-volume">Volume by month opened</h2>
          <p className="note">Median time to close is on the right.</p>
          <VolumeBars months={months} />
        </>
      ) : null}
    </section>
  );
};

const StatusUpdateTable = ({ rows }) => (
  <table className="status">
    <thead>
      <tr>
        <th>Item</th>
        <th className="fit">Case</th>
        <th>When</th>
        <th>Status</th>
        <th>Outcome</th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 ? (
        <tr><td colSpan={5}>No status update items.</td></tr>
      ) : rows.map((row, index) => {
        const numbers = Array.isArray(row.caseNumbers) && row.caseNumbers.length
          ? row.caseNumbers
          : [row.caseNumber || DASH];
        return (
          <tr className={row.isOpen ? 'open' : undefined} key={`${row.topic}-${index}`}>
            <td>{row.topic || DASH}</td>
            <td className="fit">
              {numbers.map((n, i) => <React.Fragment key={`${n}-${i}`}>{i > 0 && <br />}{n || DASH}</React.Fragment>)}
            </td>
            <td>{whenLines(row).map((line, i) => <React.Fragment key={line}>{i > 0 && <br />}{line}</React.Fragment>)}</td>
            <td>{row.status || DASH}</td>
            <td>{row.outcome || DASH}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const StillOpenTable = ({ rows }) => (
  <table className="open">
    <thead>
      <tr><th>Case</th><th>Opened</th><th>Status</th><th>Owner</th><th>Latest detail</th></tr>
    </thead>
    <tbody>
      {rows.length === 0 ? (
        <tr><td colSpan={5}>No open cases.</td></tr>
      ) : rows.map((row, index) => (
        <tr className="open" key={`${row.caseNumber}-${index}`}>
          <td>{row.caseNumber || ''}<div className="note">{row.topic || ''}</div></td>
          <td>{fmtDay(row.opened)}<div className="note">{row.sev || DASH}</div></td>
          <td>{shortOpenStatus(row)}</td>
          <td>{row.adoAssignedTo || DASH}</td>
          <td>{cleanOpenDetail(row)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SymptomTable = ({ az }) => {
  const pre = az.pre || {};
  const post = az.post || {};
  return (
    <RateTable
      rows={az.categories || []}
      labelHead="Symptom"
      firstLabel="Before Azure"
      secondLabel="After Azure"
      renderName={(row) => <CategoryName name={row.category} />}
      total={(
        <tr>
          <td><strong>All client cases</strong></td>
          <td><strong>{fmtInt(pre.count)}</strong></td>
          <td><strong>{fmt1(pre.per30)}</strong></td>
          <td><strong>{fmtInt(post.count)}</strong></td>
          <td><strong>{fmt1(post.per30)}</strong></td>
          <td />
        </tr>
      )}
    />
  );
};

export const AzureDetail = ({ briefing }) => {
  const az = briefing.azure || {};
  const cutover = fmtDayLong(briefing.periods.azureCutover);
  return (
    <section>
      <nav className="toc">
        <a href="#az-update">Status update</a>
        <a href="#az-mix">By symptom</a>
        <a href="#az-pace">Resolution pace</a>
        <a href="#az-themes">Themes</a>
        <a href="#az-outages">Availability</a>
        <a href="#az-open">Still open</a>
      </nav>
      <h2 id="az-update">Status update</h2>
      <p className="note">
        {(briefing.statusUpdate || []).length === 0
          ? 'No status update items.'
          : 'Key Connect issues since the Azure move.'}
      </p>
      <StatusUpdateTable rows={briefing.statusUpdate || []} />

      <h2 id="az-mix">By symptom</h2>
      <p className="note">Rates are per 30 days. {cutover} is counted in After Azure.</p>
      <SymptomTable az={az} />

      <h2 id="az-pace">Resolution pace</h2>
      <p className="note">Closed client cases by how long they stayed open. Dark bar is still open after two weeks.</p>
      <div className="pace">
        <PaceColumn pace={az.pace && az.pace.pre} title="Before Azure" />
        <PaceColumn pace={az.pace && az.pace.post} title="After Azure" />
      </div>

      <h2 id="az-themes">Themes</h2>
      <RateTable
        rows={az.themes || []}
        labelHead="Theme"
        firstLabel="Before Azure"
        secondLabel="After Azure"
        emptyText="No theme data for this period."
      />

      <h2 id="az-outages">Availability</h2>
      <AvailabilityTable rows={briefing.availability && briefing.availability.y26} />

      <h2 id="az-open">Still open</h2>
      <p className="note">{openCountText((briefing.openNow || []).length)}</p>
      <StillOpenTable rows={briefing.openNow || []} />
    </section>
  );
};

